import React, { useEffect, useRef, useState } from 'react';
import { useLoginMutation } from '../../../redux/auth/authApi';
import { useDispatch } from 'react-redux';
import { setAuthState } from '../../../redux/auth/authSlice';
import { LoginCredential, UserAuthResponse } from '@handover/shared-types';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid2 from '@mui/material/Grid2';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import BasePage from '../authentication-layout';
import ProjectLogo from '../../../components/commons/ProjectLogo';
import { projectConfig } from '../../../config/projectConfig';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { useTranslation } from 'react-i18next';
import { saveToken } from '../../../helpers/tokenStorage';
import {
  getAuthErrorMessageKey,
  getTwoFactorErrorMessage,
  isErrorResponse,
  isFirebaseError,
  AuthError,
} from '../../../helpers/authErrorHelper';
import { COMPANY_REGISTRATION_ENABLED } from '../../../constants/constant';
import {
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
  signOut as signOutFirebaseUser,
} from 'firebase/auth';
import { firebaseAuth } from '../../../config/firebaseClient';

const RECAPTCHA_CONTAINER_ID = 'login-mfa-recaptcha';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    backgroundColor: 'rgba(237, 230, 220, 0.04)',
    '& fieldset': {
      borderColor: 'rgba(201, 149, 106, 0.16)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(201, 149, 106, 0.28)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#C9956A',
    },
  },
  '& .MuiInputBase-input': {
    color: '#EDE6DC',
  },
  '& .MuiInputLabel-root': {
    color: '#C9956A',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#E2B88F',
  },
};

const LoginPage = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState<LoginCredential>({
    email: '',
    password: '',
  });
  const [message, setMessage] = useState<string | undefined>();
  const [messageType, setMessageType] = useState<'success' | 'error'>('error');
  const [messageTimestamp, setMessageTimestamp] = useState<number>(0);
  const [login, { isLoading: isExchangingSession }] = useLoginMutation();
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);
  const [mfaPhoneLabel, setMfaPhoneLabel] = useState<string>('');
  const [isSendingMfaCode, setIsSendingMfaCode] = useState(false);
  const [isVerifyingMfaCode, setIsVerifyingMfaCode] = useState(false);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userAuth } = useAppSelector(state => state.auth);

  const finishLogin = (response: UserAuthResponse) => {
    const { userAuth: authenticatedUser, token } = response;

    if (token) {
      const saved = saveToken(token);
      if (!saved) {
        setMessageType('error');
        setMessage('Warning: Login successful but session may not persist. Please try again.');
      }
    }

    sessionStorage.removeItem('loginErrorMessage');
    sessionStorage.removeItem('loginErrorTimestamp');

    if (!authenticatedUser) {
      setMessageType('error');
      setMessage('Login succeeded but no user profile was returned.');
      return;
    }

    dispatch(setAuthState({ userAuth: authenticatedUser }));
    navigate('/my-work', { replace: true });
  };

  const clearMfaState = () => {
    setMfaResolver(null);
    setMfaVerificationId(null);
    setMfaPhoneLabel('');
    setTwoFactorCode('');
    setIsSendingMfaCode(false);
    setIsVerifyingMfaCode(false);
  };

  const getRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    const verifier = new RecaptchaVerifier(firebaseAuth, RECAPTCHA_CONTAINER_ID, {
      size: 'invisible',
    });
    recaptchaVerifierRef.current = verifier;
    return verifier;
  };

  const resetRecaptcha = () => {
    recaptchaVerifierRef.current?.clear();
    recaptchaVerifierRef.current = null;
  };

  const signOutFirebaseSafely = async () => {
    if (firebaseAuth.currentUser) {
      await signOutFirebaseUser(firebaseAuth);
    }
  };

  const exchangeFirebaseSession = async (idToken: string) => {
    try {
      return await login({ idToken }).unwrap();
    } catch (error) {
      await signOutFirebaseSafely();
      throw error;
    }
  };

  const beginSmsChallenge = async (resolver: MultiFactorResolver) => {
    const hint = resolver.hints.find(candidate => 'phoneNumber' in candidate) || resolver.hints[0];
    if (!hint) {
      throw new Error('auth/multi-factor-info-not-found');
    }

    const hintPhoneNumber =
      typeof (hint as { phoneNumber?: unknown }).phoneNumber === 'string'
        ? ((hint as { phoneNumber?: string }).phoneNumber as string)
        : '';

    setIsSendingMfaCode(true);
    try {
      const verifier = getRecaptchaVerifier();
      await verifier.render();
      const provider = new PhoneAuthProvider(firebaseAuth);
      const verificationId = await provider.verifyPhoneNumber(
        {
          multiFactorHint: hint,
          session: resolver.session,
        },
        verifier
      );

      setMfaResolver(resolver);
      setMfaVerificationId(verificationId);
      setMfaPhoneLabel(hintPhoneNumber || credentials.email);
      setMessageType('success');
      setMessage(`Enter the 6-digit verification code sent to ${hintPhoneNumber || 'your phone'}.`);
    } finally {
      setIsSendingMfaCode(false);
    }
  };

  const handleLegacyLoginFallback = async () => {
    const response = await login(credentials).unwrap();

    if (response.message && !response.userAuth) {
      setMessageType(response.mfaRequired ? 'error' : 'success');
      setMessage(
        response.mfaRequired
          ? 'This account still requires the previous two-factor flow. Please contact support.'
          : response.message
      );
      return;
    }

    finishLogin(response as UserAuthResponse);
  };

  const showAuthError = (error: AuthError, fallbackMessage: string) => {
    setMessageType('error');
    const timestamp = Date.now();
    setMessageTimestamp(timestamp);

    let errorMessage = '';

    if (isErrorResponse(error)) {
      if (error.data?.messageKey) {
        errorMessage = t(error.data.messageKey);
      } else if (error.data?.message) {
        const msg = error.data.message;
        errorMessage = msg.includes('.') ? t(msg) : msg;
      } else {
        errorMessage = fallbackMessage;
      }
    } else if (isFirebaseError(error)) {
      errorMessage = getTwoFactorErrorMessage(error, t(getAuthErrorMessageKey(error)));
    } else {
      errorMessage = fallbackMessage;
    }

    setMessage(errorMessage);
    sessionStorage.setItem('loginErrorMessage', errorMessage);
    sessionStorage.setItem('loginErrorTimestamp', timestamp.toString());
  };

  useEffect(() => {
    const forceLogoutMessage = sessionStorage.getItem('forceLogoutMessage');
    if (forceLogoutMessage) {
      setMessageType('error');
      setMessage(forceLogoutMessage);
      sessionStorage.removeItem('forceLogoutMessage');
    }

    const loginErrorMessage = sessionStorage.getItem('loginErrorMessage');
    const loginErrorTimestamp = sessionStorage.getItem('loginErrorTimestamp');
    if (loginErrorMessage && loginErrorTimestamp) {
      setMessageType('error');
      setMessage(loginErrorMessage);
      setMessageTimestamp(Number(loginErrorTimestamp));
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const prefilledEmail = searchParams.get('email');
    const setupStatus = searchParams.get('setup');
    const inviteStatus = searchParams.get('invite');

    if (prefilledEmail) {
      setCredentials(prev => ({
        ...prev,
        email: prev.email || prefilledEmail,
      }));
    }

    if (setupStatus === 'success') {
      setMessageType('success');
      setMessage('Password set successfully. Sign in to continue.');
    }

    if (inviteStatus === 'accepted') {
      setMessageType('success');
      setMessage('Account setup complete. Sign in to continue.');
    }
  }, [location.search]);

  useEffect(() => {
    if (userAuth) {
      navigate('/my-work', { replace: true });
    }
  }, [navigate, userAuth]);

  useEffect(() => {
    if (messageTimestamp > 0) {
      const elapsed = Date.now() - messageTimestamp;
      const remainingTime = Math.max(0, 5000 - elapsed);

      const timer = setTimeout(() => {
        sessionStorage.removeItem('loginErrorMessage');
        sessionStorage.removeItem('loginErrorTimestamp');
      }, remainingTime);

      return () => clearTimeout(timer);
    }
  }, [messageTimestamp]);

  useEffect(
    () => () => {
      resetRecaptcha();
    },
    []
  );

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const trimmedEmail = credentials.email.trim().toLowerCase();
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        trimmedEmail,
        credentials.password
      );

      if (!userCredential.user.emailVerified) {
        await signOutFirebaseSafely();
        await handleLegacyLoginFallback();
        return;
      }

      const idToken = await userCredential.user.getIdToken(true);
      const response = await exchangeFirebaseSession(idToken);
      finishLogin(response);
    } catch (error) {
      const authError = error as AuthError;

      if (isFirebaseError(authError) && authError.code === 'auth/multi-factor-auth-required') {
        try {
          const resolver = getMultiFactorResolver(firebaseAuth, authError as never);
          await beginSmsChallenge(resolver);
        } catch (mfaError) {
          showAuthError(mfaError as AuthError, 'We could not send the verification code. Please try again.');
        }
        return;
      }

      showAuthError(authError, t('auth.signin.error.invalid.credential'));
    }
  };

  const handleVerifyTwoFactor = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!mfaResolver || !mfaVerificationId) {
      setMessageType('error');
      setMessage('Your sign-in session expired. Please sign in again.');
      return;
    }

    setIsVerifyingMfaCode(true);
    try {
      const credential = PhoneAuthProvider.credential(mfaVerificationId, twoFactorCode);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const userCredential = await mfaResolver.resolveSignIn(assertion);
      const idToken = await userCredential.user.getIdToken(true);
      const response = await exchangeFirebaseSession(idToken);
      clearMfaState();
      finishLogin(response);
    } catch (error) {
      showAuthError(error as AuthError, 'The verification code was not accepted. Please try again.');
    } finally {
      setIsVerifyingMfaCode(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (message && messageType === 'error') {
      const timeSinceMessage = Date.now() - messageTimestamp;
      const minimumDisplayTimeMs = 5000;

      if (timeSinceMessage >= minimumDisplayTimeMs) {
        setMessage(undefined);
      }
    }

    setCredentials({
      ...credentials,
      [event.target.name]: event.target.value,
    });
  };

  const isSubmitting = isExchangingSession || isSendingMfaCode;

  return (
    <BasePage image={projectConfig.bgLogin}>
      <Grid2 container justifyContent="center" alignItems="center" minHeight="100vh">
        <Grid2 size={{ xs: 11, sm: 9, md: 6, lg: 4, xl: 3.5 }}>
          <Card
            sx={{
              position: 'relative',
              borderRadius: 4,
              px: { xs: 0.5, md: 1 },
              py: 3.5,
              border: `1px solid ${alpha('#C9956A', 0.18)}`,
              background: 'linear-gradient(180deg, rgba(35, 31, 26, 0.96) 0%, rgba(28, 24, 20, 0.98) 100%)',
              color: '#EDE6DC',
              boxShadow: '0px 18px 48px rgba(12, 9, 7, 0.42)',
              backdropFilter: 'blur(14px)',
            }}
          >
            <Box width="100%" textAlign="center" px={3}>
              <Chip
                label="Handover Engine"
                sx={{
                  borderRadius: 999,
                  mb: 2,
                  bgcolor: alpha('#C9956A', 0.12),
                  color: '#E2B88F',
                  border: `1px solid ${alpha('#C9956A', 0.22)}`,
                }}
              />
              <Typography
                sx={{
                  color: '#F6EFE6',
                  mt: 0.5,
                  mb: 0.75,
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: { xs: '2.25rem', md: '2.85rem' },
                }}
              >
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Sign in to continue.
              </Typography>
              <Grid2 container justifyContent="center" sx={{ mt: 2, mb: 2.5 }}>
                <ProjectLogo size="card" />
              </Grid2>
            </Box>

            <Box px={3}>
              <Box component="form" role="form" onSubmit={mfaResolver ? handleVerifyTwoFactor : handleLogin} noValidate>
                {!mfaResolver ? (
                  <>
                    <Box mb={1}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Email Address"
                        autoFocus
                        type="email"
                        name="email"
                        value={credentials.email}
                        onChange={handleChange}
                        sx={inputSx}
                      />
                    </Box>
                    <Box mb={1}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Password"
                        type="password"
                        name="password"
                        value={credentials.password}
                        onChange={handleChange}
                        sx={inputSx}
                      />
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Box mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Two-factor authentication is enabled for {credentials.email}.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Verification code destination: {mfaPhoneLabel || 'your phone'}
                      </Typography>
                    </Box>
                    <Box mb={1}>
                      <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="SMS Verification Code"
                        autoFocus
                        value={twoFactorCode}
                        onChange={event => setTwoFactorCode(event.target.value.replace(/\D+/g, '').slice(0, 6))}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                        sx={inputSx}
                      />
                    </Box>
                    <Box mt={3} display="flex" gap={1}>
                      <Button
                        type="button"
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          clearMfaState();
                          resetRecaptcha();
                          setMessage(undefined);
                        }}
                        sx={{
                          borderColor: alpha('#C9956A', 0.22),
                          color: '#EDE6DC',
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isVerifyingMfaCode || twoFactorCode.length < 6}
                        fullWidth
                        variant="contained"
                        sx={{
                          py: 1.15,
                          backgroundColor: '#C9956A',
                          color: '#1C1814',
                          '&:hover': {
                            backgroundColor: '#B8845C',
                          },
                        }}
                      >
                        {isVerifyingMfaCode ? 'Verifying...' : 'Verify Code'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {message && (
                  <Alert
                    severity={messageType}
                    sx={{
                      mt: 2,
                      borderRadius: 2.5,
                      bgcolor:
                        messageType === 'success'
                          ? alpha('#70B690', 0.12)
                          : alpha('#E57A73', 0.12),
                      border: `1px solid ${
                        messageType === 'success' ? alpha('#70B690', 0.24) : alpha('#E57A73', 0.22)
                      }`,
                    }}
                  >
                    {message}
                  </Alert>
                )}

                {!mfaResolver && (
                  <Box mt={4} mb={1}>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      variant="contained"
                      sx={{
                        py: 1.15,
                        backgroundColor: '#C9956A',
                        color: '#1C1814',
                        '&:hover': {
                          backgroundColor: '#B8845C',
                        },
                      }}
                    >
                      {isSubmitting ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </Box>
                )}

                {!mfaResolver && (
                  <Stack spacing={0.75} sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      Contact your manager if access is not active.
                    </Typography>
                  </Stack>
                )}

                {COMPANY_REGISTRATION_ENABLED && (
                  <Box textAlign="center" mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      New company?{' '}
                      <Link
                        component={RouterLink}
                        to="/auth/register-company"
                        underline="hover"
                        sx={{ color: '#E2B88F' }}
                      >
                        Create your workspace
                      </Link>
                    </Typography>
                  </Box>
                )}

                <Box id={RECAPTCHA_CONTAINER_ID} sx={{ display: 'none' }} />
              </Box>
            </Box>
          </Card>
        </Grid2>
      </Grid2>
    </BasePage>
  );
};

export default LoginPage;
